import { Tooltip, Typography, makeStyles, IconButton } from "@material-ui/core"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"

import { ClientFragment } from "../../generated/graphql"
import TimeDisplay from "../atoms/TimeDisplay"
import { colors } from "../../utils/theme"

type ClientLocalTimeDisplayProps = {
  client: ClientFragment
  tooltipText?: string
  label?: string
}

/**
 * Displays the current local time for a given client, based on their most recent timezone.
 *
 * Renders a styled inline container showing a label, the live time via `TimeDisplay`,
 * and an info icon with a tooltip. The tooltip describes whose time is shown and
 * which timezone is being used.
 *
 * @param client - A `ClientFragment` object used to derive the client's timezone and display name.
 * @param tooltipText - Optional override for the tooltip message. Defaults to a message
 *   including the client's name and timezone.
 * @param label - Text label shown before the time value. Defaults to `"Client's Time"`.
 */
export default function ClientLocalTimeDisplay({
  client,
  tooltipText,
  label = "Client's Time",
}: ClientLocalTimeDisplayProps) {
  const classes = useStyles()
  const clientTimezone = client.most_recent_timezone
  const clientName =
    [client.first_name, client.last_name].filter(Boolean).join(" ") ||
    client.full_name ||
    "Your Client"

  const tooltipMessage =
    tooltipText ??
    `${clientName ? `${clientName}'s` : "Client's"} local time (${
      clientTimezone ?? "Their time zone"
    })`

  return (
    <div className={classes.container}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.timeValue}>
        <TimeDisplay timezone={clientTimezone ?? undefined} />
      </Typography>
      <Tooltip arrow title={tooltipMessage} placement="top">
        <IconButton
          className={classes.infoButton}
          size="small"
          aria-label={tooltipMessage}
        >
          <InfoOutlinedIcon className={classes.infoIcon} />
        </IconButton>
      </Tooltip>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 4,
    backgroundColor: colors.primary.cloud_05,
    width: "fit-content",
    marginBottom: theme.spacing(1),
  },
  label: {
    color: colors.neutral.denim,
    fontWeight: 600,
    fontSize: 16,
    letterSpacing: 0.4,
  },
  timeValue: {
    color: colors.primary.midnight,
    fontWeight: 700,
  },
  infoButton: {
    padding: 2,
    marginLeft: 2,
  },
  infoIcon: {
    fontSize: 16,
    color: colors.neutral.denim,
  },
}))
